// client/src/components/pages/Settings/loadMatchingSetting.ts
//
// ST-00218 P3: a route grouping's "Load matching" setting (route_groupings.bfcf_match_mode,
// migration 058). One setting replaces "BF/CF Ticket Matching": Truck + time (the load-matching
// engine) is the default; the three delivery-number options are the matching of before.
// Contract: scratchpad/st00218/API_P3.md §1.
// ST-00298: a Truck + time route also has a fallback (route_groupings.bfcf_fallback_mode,
// migration 079): the delivery-number option or None it shows while load matching is unavailable.

import type { LegacyMethod, MatchingMethod } from '../../../types/routeFigures';

export interface LoadMatchingOption {
  value: MatchingMethod;
  label: string;
  /** Plain-language help shown under the select for the chosen option. */
  help: string;
  recommended?: boolean;
}

export const LOAD_MATCHING_OPTIONS: readonly LoadMatchingOption[] = [
  {
    value: 'engine',
    label: 'Truck + time, with the delivery number (recommended)',
    help: 'Pairs each receipt with its dispatch by the delivery number and the truck registration. Where the delivery number is blank or wrong, it pairs them by the truck and the time, within the route’s transit window. Keying errors are flagged for review.',
    recommended: true,
  },
  {
    value: 'exact',
    label: 'Delivery number (exact)',
    help: 'A receipt’s Delivery No must equal the dispatch Ticket No exactly.',
  },
  {
    value: 'numeric',
    label: 'Delivery number (numeric part)',
    help: 'Compares only the number: the prefix and leading zeros are removed, so NDB-22178 matches 22178.',
  },
  {
    value: 'none',
    label: 'None (period totals only)',
    help: 'No Brought Forward or Carried Forward: the route shows its period totals only.',
  },
];

/** Short labels for places that name the method (Route Summary sub-labels, notes). */
export const LOAD_MATCHING_SHORT_LABELS: Record<MatchingMethod, string> = {
  engine: 'truck + time',
  exact: 'delivery number (exact)',
  numeric: 'delivery number (numeric part)',
  none: 'none',
};

/** Shown under the setting: what happens while the ledger is not available. */
export const LOAD_MATCHING_FALLBACK_HELP =
  'While load matching is switched off or catching up, this route uses the matching chosen below instead, with a note on the page.';

/** ST-00298: the options of "If load matching is unavailable, use" (every option but Truck + time). */
export const FALLBACK_OPTIONS: readonly LoadMatchingOption[] = LOAD_MATCHING_OPTIONS.filter(
  (o) => o.value !== 'engine'
);

const METHODS = new Set<string>(LOAD_MATCHING_OPTIONS.map((o) => o.value));
const FALLBACKS = new Set<string>(FALLBACK_OPTIONS.map((o) => o.value));

/** A stored value as the setting shows it (anything unknown is Delivery number (exact)). */
export const normaliseMatchingMethod = (value: unknown): MatchingMethod =>
  typeof value === 'string' && METHODS.has(value) ? (value as MatchingMethod) : 'exact';

/** The default of a new grouping. */
export const DEFAULT_MATCHING_METHOD: MatchingMethod = 'engine';

/** A stored fallback as the setting shows it (anything unknown is Delivery number (exact)). */
export const normaliseFallbackMethod = (value: unknown): LegacyMethod =>
  typeof value === 'string' && FALLBACKS.has(value) ? (value as LegacyMethod) : 'exact';

/** The fallback of a new grouping. */
export const DEFAULT_FALLBACK_METHOD: LegacyMethod = 'exact';
